import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';

/**
 * Generate a URL-safe slug from club name
 */
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Generate unique slug - append random suffix if needed
 */
async function generateUniqueSlug(baseSlug) {
  let slug = baseSlug || 'club';
  let attempts = 0;
  const maxAttempts = 10;

  while (attempts < maxAttempts) {
    const existing = await getClubBySlug(slug);
    if (!existing) return slug;
    slug = `${baseSlug || 'club'}-${Math.random().toString(36).slice(2, 8)}`;
    attempts++;
  }
  return `${baseSlug || 'club'}-${Date.now()}`;
}

/**
 * Create a new club
 */
export async function createClub(ownerId, clubData) {
  const baseSlug = slugify(clubData.name || 'club');
  const slug = await generateUniqueSlug(baseSlug);

  const clubRef = doc(collection(db, 'clubs'));
  const clubId = clubRef.id;

  const club = {
    id: clubId,
    name: clubData.name || 'My Club',
    ownerId,
    slug,
    markupPerMonth: clubData.markupPerMonth ?? 0,
    stripeAccountId: null,
    w9Status: null,
    w9StoragePath: null,
    bannerUrl: null,
    logoUrl: null,
    macrocycleSettings: clubData.macrocycleSettings ?? {
      offSeason: 3,
      preSeason: 2,
      inSeason: 2,
    },
    practiceDays: clubData.practiceDays ?? [],
    teams: clubData.teams ?? [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(clubRef, club);

  // Update user with ownedClubIds and role
  const { getUserProfile, updateUserProfile } = await import('./database');
  const existing = await getUserProfile(ownerId);
  const existingIds = existing?.ownedClubIds || [];
  const newIds = existingIds.includes(clubId) ? existingIds : [...existingIds, clubId];
  await updateUserProfile(ownerId, {
    ownedClubIds: newIds,
    role: 'club_owner',
  });

  return { clubId, slug, club: { ...club, id: clubId } };
}

/**
 * Get club by ID
 */
export async function getClub(clubId) {
  if (!clubId) return null;
  const snap = await getDoc(doc(db, 'clubs', clubId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Get club by slug
 */
export async function getClubBySlug(slug) {
  if (!slug) return null;
  const q = query(
    collection(db, 'clubs'),
    where('slug', '==', slug)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Update club
 */
export async function updateClub(clubId, updates) {
  const clubRef = doc(db, 'clubs', clubId);
  await updateDoc(clubRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
  return getClub(clubId);
}

/**
 * Create club membership (player joins club)
 */
export async function createClubMembership(userId, clubId, year) {
  const expiresAt = new Date(year, 11, 31); // End of year
  const membershipRef = await addDoc(collection(db, 'club_memberships'), {
    userId,
    clubId,
    year,
    status: 'active',
    stripeSubscriptionId: null, // Placeholder until Stripe
    joinedAt: serverTimestamp(),
    expiresAt: expiresAt.toISOString().split('T')[0],
  });
  return membershipRef.id;
}

/**
 * Get active memberships for a club
 */
export async function getClubMemberships(clubId, status = 'active') {
  const q = query(
    collection(db, 'club_memberships'),
    where('clubId', '==', clubId),
    where('status', '==', status)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get user's club membership (if any)
 */
export async function getUserClubMembership(userId, clubId) {
  const q = query(
    collection(db, 'club_memberships'),
    where('userId', '==', userId),
    where('clubId', '==', clubId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Get clubs owned by user
 */
export async function getClubsByOwner(ownerId) {
  const q = query(
    collection(db, 'clubs'),
    where('ownerId', '==', ownerId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================
// TEAM SWITCHING / ACTIVE MEMBERSHIP
// ============================================

/**
 * Get a user's current active membership (club or coach)
 */
export async function getActiveUserMembership(userId) {
  const q = query(
    collection(db, 'club_memberships'),
    where('userId', '==', userId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Deactivate all active memberships for a user
 */
export async function deactivateUserMemberships(userId) {
  const q = query(
    collection(db, 'club_memberships'),
    where('userId', '==', userId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  const updates = snap.docs.map((d) =>
    updateDoc(doc(db, 'club_memberships', d.id), {
      status: 'inactive',
      deactivatedAt: serverTimestamp(),
    })
  );
  await Promise.all(updates);
  return snap.size;
}

// ============================================
// TEAM MANAGEMENT
// ============================================

/**
 * Update the teams array on a club document.
 * Each team: { id, ageGroup, label }
 */
export async function updateClubTeams(clubId, teams) {
  const clubRef = doc(db, 'clubs', clubId);
  await updateDoc(clubRef, {
    teams,
    updatedAt: serverTimestamp(),
  });
  return getClub(clubId);
}

// ============================================
// PER-TEAM SCHEDULE MANAGEMENT
// ============================================

/**
 * Get all team schedules for a club.
 * Stored as subcollection: clubs/{clubId}/teamSchedules/{teamId}
 */
export async function getTeamSchedules(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'teamSchedules'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a single team schedule.
 */
export async function getTeamSchedule(clubId, teamId) {
  const snap = await getDoc(doc(db, 'clubs', clubId, 'teamSchedules', teamId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Create or update a team schedule.
 * @param {string} clubId
 * @param {string} teamId - matches the team.id from club.teams
 * @param {object} data - { practiceDays, gameDays, strengthDaysPerWeek, strengthDays, season }
 */
export async function updateTeamSchedule(clubId, teamId, data) {
  const scheduleRef = doc(db, 'clubs', clubId, 'teamSchedules', teamId);
  await setDoc(scheduleRef, {
    teamId,
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return getTeamSchedule(clubId, teamId);
}
